import _ from 'lodash';

/**
 * Check if a value matches a pattern, including wildcard support
 * @param tagValue {string} - The value to check
 * @param criteriaValue {string} - The pattern to match against
 * @returns {boolean}
 */
const matchesPattern = (tagValue, criteriaValue) => {
  // Handle null/undefined values
  if (!tagValue || !criteriaValue) return false;

  // If criteriaValue starts with *, do partial match
  if (criteriaValue.startsWith('*')) {
    const pattern = criteriaValue.slice(1).toLowerCase();
    return tagValue.toLowerCase().includes(pattern);
  }

  // Otherwise do exact match
  return tagValue === criteriaValue;
};

/**
 * Enhanced portfolio filter that handles both inclusion and exclusion criteria with wildcard support
 * @param portfolios {Array<Object>}
 * @param criteria {{ include?: Object, exclude?: Object }} - Criteria to filter portfolios
 * @returns {{selected: Object|null, remaining: Array<Object>}}
 */
const filterAndPickV2 = (portfolios, criteria = {}) => {
  const { include = {}, exclude = {} } = criteria;

  const filtered = portfolios.filter(portfolio => {
    const tags = _.get(portfolio, 'tags', {});

    // Check inclusion criteria
    const matchesInclude = _.isEmpty(include) ||
      Object.entries(include).every(([key, value]) => {
        const tagValue = tags[key];

        if (Array.isArray(value)) {
          // If criteria is an array, match any of the values
          return value.some(criteriaValue =>
            matchesPattern(tagValue, criteriaValue)
          );
        }

        // Handle single value
        return matchesPattern(tagValue, value);
      });

    // Check exclusion criteria
    const matchesExclude = !_.isEmpty(exclude) &&
      Object.entries(exclude).some(([key, value]) => {
        const tagValue = tags[key];

        if (Array.isArray(value)) {
          // If criteria is an array, match any of the values
          return value.some(criteriaValue =>
            matchesPattern(tagValue, criteriaValue)
          );
        }

        // Handle single value
        return matchesPattern(tagValue, value);
      });

    return matchesInclude && !matchesExclude;
  });

  const selected = _.sample(filtered) || null;
  const remaining = selected ?
    portfolios.filter(p => p.id !== selected.id) :
    portfolios;

  return { selected, remaining };
};
/**
 * Select fixed income portfolio
 */
const selectFixedIncome = (portfolios, equity) => {
  if (equity >= 99) {
    return { selected: null, remaining: portfolios };
  }
  return filterAndPickV2(portfolios, {
    include: { asset_class: ['fixed', 'other'], category: ['bond', 'preferred stock', 'convertibles', '*core bond'] },
    exclude: { asset_type: ['stock'] }
  });
};

/**
 * Select dividend stock portfolio
 */
const selectDividendStock = (portfolios, userResponses) => {
  if (!(userResponses.equity >= 50 && userResponses.dividend)) {
    return { selected: null, remaining: portfolios };
  }
  return filterAndPickV2(portfolios, {
    include: {
      asset_type: ['stock'],
      asset_class: ['equity']
    },
  });
};

/**
 * Select growth stock portfolio
 */
const selectGrowthStock = (portfolios, userResponses) => {
  if (!(userResponses.equity >= 50)) {
    return { selected: null, remaining: portfolios };
  }
  return filterAndPickV2(portfolios, {
    include: {
      asset_class: ['equity'],
      style: ['growth']
    }
  });
};

/**
 * Select international portfolio
 */
const selectInternational = (portfolios, userResponses) => {
  if (!userResponses.international) {
    return { selected: null, remaining: portfolios };
  }
  return filterAndPickV2(portfolios, {
    include: { asset_class: ['equity'] },
    exclude: { region: ['north america'] }
  });
};

/**
 * Select domestic ETF
 */
const selectDomesticETF = (portfolios, userResponses) => {
  const shouldSelectETF = (
    (!userResponses.singleStock && !userResponses.dividend && !userResponses.international && userResponses.equity > 0) ||
    (userResponses.equity > 50 && (userResponses.singleStock === false || userResponses.dividend === false))
  );

  if (!shouldSelectETF) {
    return { selected: null, remaining: portfolios };
  }

  return filterAndPickV2(portfolios, {
    include: {
      asset_class: ['equity'],
      asset_type: ['etf'],
      region: ['north america']
    }
  });
};

/**
 * Select tilted portfolio
 */
const selectTilt = (portfolios, userResponses) => {
  if (!['growth_tilt', 'value_tilt'].includes(userResponses.tilt)) {
    return { selected: null, remaining: portfolios };
  }

  const tiltStyle = userResponses.tilt === 'growth_tilt' ? 'growth' : 'value';
  return filterAndPickV2(portfolios, {
    include: {
      style: tiltStyle,
      asset_class: 'equity'
    }
  });
};

/**
 * Apply the rules to select portfolios based on user responses
 * @param portfolios {Array<Object>} - List of portfolios to select from
 * @param userResponses {Object} - User responses to the form
 * @returns {*}
 */
const applyRules = (portfolios, userResponses) => {
  let selectedPortfolios = [];
  let remainingPortfolios = [...portfolios];

  // Define selection steps in order of priority
  const selectionSteps = [
    { fn: selectFixedIncome, args: [userResponses.equity] },
    { fn: selectDividendStock, args: [userResponses] },
    { fn: selectGrowthStock, args: [userResponses] },
    { fn: selectInternational, args: [userResponses] },
    { fn: selectDomesticETF, args: [userResponses] },
    { fn: selectTilt, args: [userResponses] }
  ];

  // Execute each selection step sequentially
  for (const step of selectionSteps) {
    const { selected, remaining } = step.fn(remainingPortfolios, ...step.args);
    if (selected) {
      selectedPortfolios.push(selected);
      remainingPortfolios = remaining;
    }
  }

  return selectedPortfolios;
};


export { applyRules }